import React, { FC, useMemo, useState } from 'react';
import { Alert, Radio, Checkbox, Fieldset, useRadioGroup } from '@innovationnorway/cl-react';
import { FormStepButtons } from '../FormStepButtons';
import { SelfClassification, SelfDeclarationDto, SivaPhase } from 'src/api/v2';
import { useTranslation } from 'react-i18next';

type Props = {
  organizationNumber: string;
  companyName: string;
  availablePhases: ReadonlyArray<SivaPhase>;
  setDeclaration: (value: SelfDeclarationDto) => void;
  stepBackward: (() => void) | undefined;
  stepForward: (() => void) | undefined;
  required: boolean;
};

const NewCompanyDeclaration: FC<Props> = ({
  organizationNumber,
  companyName,
  setDeclaration,
  stepBackward,
  stepForward,
  required,
}) => {
  const { t: tSiva } = useTranslation('siva');
  const [hasDeclaration, setHasDeclaration] = useState<boolean | null>(null);
  const [declarationType, setDeclarationType] = useState<Set<SelfClassification> | null>(null);

  const handleStepForward = () => {
    if (hasDeclaration) {
      setDeclaration({
        organizationNumber,
        financialYear: new Date().getFullYear(),
        companyName,
        selfDeclarationDelivered: hasDeclaration === true,
        selfClassification: declarationType ? Array.from(declarationType) : [],
      });
    }
    stepForward?.();
  };

  const checkboxOptions = useMemo(() => {
    return [
      {
        text: tSiva('reporting.selfdeclaration.Partner'),
        value: SelfClassification.Partner,
        disabled: declarationType?.has(SelfClassification.Independent),
        selected: declarationType?.has(SelfClassification.Partner),
      },
      {
        text: tSiva('reporting.selfdeclaration.Associated'),
        value: SelfClassification.Associated,
        disabled: declarationType?.has(SelfClassification.Independent),
        selected: declarationType?.has(SelfClassification.Associated),
      },
      {
        text: tSiva('reporting.selfdeclaration.Independent'),
        value: SelfClassification.Independent,
        disabled:
          declarationType?.has(SelfClassification.Partner) ||
          declarationType?.has(SelfClassification.Associated),
        selected: declarationType?.has(SelfClassification.Independent) || false,
      },
    ];
  }, [declarationType, tSiva]);

  const { getRadioProps } = useRadioGroup({
    name: 'selfDeclaration',
    onChange: (value) => (value === 'yes' ? setHasDeclaration(true) : setHasDeclaration(false)),
    value: undefined,
  });

  return (
    <div>
      <Fieldset>
        <Fieldset.Legend>{tSiva('reporting.selfdeclaration.Title')} *</Fieldset.Legend>
        <Radio label="Ja" name="selfDeclaration" {...getRadioProps('yes')} />
        <Radio label="Nei" name="selfDeclaration" {...getRadioProps('no')} />
      </Fieldset>

      {hasDeclaration === true && (
        <Fieldset>
          <Fieldset.Legend>{tSiva('reporting.selfdeclaration')} *</Fieldset.Legend>
          {checkboxOptions.map((option) => {
            return (
              <Checkbox
                key={option.value}
                label={option.text}
                disabled={option.disabled}
                onChange={() => {
                  if (option.selected) {
                    const newSet = new Set(declarationType);
                    newSet.delete(option.value);
                    return setDeclarationType(newSet.size > 0 ? newSet : null);
                  }
                  setDeclarationType((prev) => new Set(prev).add(option.value));
                }}
              />
            );
          })}
        </Fieldset>
      )}

      {hasDeclaration === false && required && (
        <Alert data-color="info" className="margin-top--2">
          {tSiva('reporting.selfdeclaration.Required')}
        </Alert>
      )}
      <FormStepButtons
        stepBackward={stepBackward}
        stepForward={handleStepForward}
        stepForwardDisabled={
          hasDeclaration === null ||
          (hasDeclaration && declarationType === null) ||
          (declarationType === null && required)
        }
      />
    </div>
  );
};

export default NewCompanyDeclaration;
